import { getConfigForImportedTest, getConfigForMeasuredTest, getConfigForTest } from 'Components/Worksheets/Config';
import { IConfigFunctionArgs } from 'Components/Worksheets/Core';
import { mergeConfig } from 'Components/Worksheets/Core/Helpers/configHelpers';
import { FormatTypes } from '../../../../../Lib/Format';
import * as oPath from '../../../../../Lib/Object/Path/Immutable';
import externalTestMethods from '../../../ExternalTestMethods';
import { getMoistureContentPercent } from '../../../MoistureContentWorksheet/Aashto/Config';
import qestIds from '../../../QestIds';
import getDerivedFromMeasurementsConfig from '../../derivedFromMeasurementsConfig';
import getMeasuredUsingConfig from '../../measuredUsingConfig';
import getLabelConfig from './labelConfig';

const isIpTest = ({ worksheetType }: IConfigFunctionArgs) =>
  [qestIds.maximumDryDensity.AASHTO_T99.rev2015, qestIds.maximumDryDensity.AASHTO_T180.rev2015].includes(
    worksheetType.Id
  );

const isWetDensityMassEntryUnitPounds = ({ worksheet }: IConfigFunctionArgs) =>
  worksheet.data.Preparation.WetDensityMassEntryUnit === 'pounds';

const getDefaultReadability = (configArgs: IConfigFunctionArgs) =>
  isIpTest(configArgs) && isWetDensityMassEntryUnitPounds(configArgs)
    ? { defaultReadability: 0.001, defaultUom: 'lb' }
    : { defaultReadability: 1, defaultUom: 'g' };

const config = () =>
  mergeConfig(
    {
      // Fields
      ...getConfigForMeasuredTest(
        qestIds.oversizeDetermination.AASHTO_T99_T180,
        'data.Preparation.AssociatedOversizeDetermination'
      ),
      ...getConfigForMeasuredTest(
        qestIds.specificGravityCoarse.AASHTO_T_85.rev2014,
        'data.Preparation.SpecificGravityCoarse.AssociatedSpecificGravityCoarse'
      ),
      ...getConfigForImportedTest(
        qestIds.psdSieveAnalysis.AASHTO_T_27.rev2020,
        'data.Preparation.AssociatedOversizeDetermination'
      ),
      'data.Preparation.OversizeDeterminationResults.Sieves.CumulativeDryMassRetainedPercent': {
        format: { nearest: 1 },
      },
      'data.Preparation.AssociatedOversizeDetermination.ExternalTest.Results.Sieves.CumulativeDryMassRetainedPercent': {
        format: { nearest: 0.1 },
      },

      ...getConfigForMeasuredTest(
        qestIds.moistureContent.AASHTO_T_265.rev2015,
        'data.Preparation.SpecificGravityCoarse.AssociatedOversizeMoisture',
        { disabledPaths: ['Preparation.VisualNominalParticleSize'] }
      ),
      ...getConfigForMeasuredTest(
        qestIds.moistureContent.AASHTO_T_265.rev2015,
        'data.Preparation.AssociatedFineMoisture',
        { disabledPaths: ['Preparation.VisualNominalParticleSize'] }
      ),
      ...getConfigForImportedTest(
        qestIds.moistureContent.AASHTO_T_265.rev2015,
        'data.Preparation.AssociatedFineMoisture'
      ),
      ...getConfigForImportedTest(
        qestIds.specificGravityCoarse.AASHTO_T_85.rev2014,
        'data.Preparation.SpecificGravityCoarse.AssociatedSpecificGravityCoarse'
      ),
      'data.Preparation.SpecificGravityCoarse.AssociatedSpecificGravityCoarse.ExternalTest.Results.SpecificGravityOvenDry':
        {
          format: { nearest: 0.001 },
        },
      'data.Preparation.SpecificGravityCoarse.AssociatedSpecificGravityCoarse.ExternalTest.Results.Absorption': {
        format: { nearest: 0.01 },
      },
      ...getConfigForImportedTest(
        qestIds.moistureContent.AASHTO_T_265.rev2015,
        'data.Preparation.SpecificGravityCoarse.AssociatedOversizeMoisture'
      ),
      //Moisture Contents
      //
      //For an imported test, we should source the moisture content method (A or B) from the source test. In normal use, it
      //*should* always be 'B', but if we import from a moisture content done in accordance with method A, the user really
      //ought to know about it because it will affect precision for any calculations that use the result.
      //
      //For an external result, the user should probably confirm that 'method b' was used (and rounding should be based on
      //the method that they selected).
      'data.Preparation.SpecificGravityCoarse.AssociatedOversizeMoisture.ExternalTest.Results.MoistureContentPercent': {
        format: { nearest: 0.1 },
      },
      'data.Preparation.AssociatedFineMoisture.ExternalTest.Results.MoistureContentPercent': {
        format: { nearest: 0.1 },
      },
      'data.Preparation.ScreeningASTM.OversizeMoistureContent': { format: { nearest: 0.1 } },
      'data.Preparation.ScreeningASTM.OversizePercent': {
        format: { nearest: 0.1 },
      },
      'data.Preparation.MinimumOversizeForCorrection': {
        format: { nearest: 0.1 },
      },
      'data.Preparation.SpecificGravityCoarse.OversizeMoisturePercent': { format: { nearest: 0.1 } },
      ...getMoistureContentPercent('data.Preparation.FineMoistureContent'),

      'data.Preparation.SpecificGravityCoarse.SpecificGravityCoarseResults.SpecificGravityOvenDry': {
        format: { nearest: 0.001 },
      },
      'data.Preparation.SpecificGravityCoarse.AssumedSpecificGravity': {
        format: { nearest: 0.001 },
      },
      'data.Preparation.SpecificGravityCoarse.AssumedAbsorption': {
        format: { nearest: 0.01 },
      },
      'data.Preparation.SpecificGravityCoarse.SpecificGravityCoarseResults.SpecificGravitySSD': {
        format: { nearest: 0.001 },
      },
      'data.Preparation.SpecificGravityCoarse.SpecificGravityCoarseResults.ApparentSpecificGravity': {
        format: { nearest: 0.001 },
      },
      'data.Preparation.SpecificGravityCoarse.SpecificGravityCoarseResults.Absorption': {
        format: { nearest: 0.01 },
      },

      //Test Fraction Specific Gravity
      ...getConfigForImportedTest(
        qestIds.specificGravityCoarse.AASHTO_T_85.rev2014,
        'data.TestFractionSpecificGravity.AssociatedSpecificGravity'
      ),
      'data.TestFractionSpecificGravity.AssociatedSpecificGravity.ExternalTest': {
        testMethods: [
          externalTestMethods.specificGravity.AASHTO_T_85,
          externalTestMethods.specificGravity.AASHTO_T_100,
        ],
      },
      'data.TestFractionSpecificGravity.AssumedSpecificGravity': {
        format: { nearest: 0.001 },
      },
      'data.TestFractionSpecificGravity.SpecificGravity': {
        format: { nearest: 0.001 },
      },
      'data.TestFractionSpecificGravity.AssociatedSpecificGravity.ExternalTest.Results.SpecificGravityOvenDry': {
        format: { nearest: 0.001 },
      },

      ...getConfigForTest(qestIds.moistureContent.AASHTO_T_265.rev2015, 'data.TestPortions.MoistureDetermination'),

      'mddChart.XAxisMajorStep': { format: { nearest: 0.1 } },
      'mddChart.XAxisMin': { format: { nearest: 0.1 } },
      'cwdChart.XAxisMajorStep': { format: { nearest: 0.1 } },
      'cwdChart.XAxisMin': { format: { nearest: 0.1 } },

      'data.grid.CompactionData': (wsDetails: IConfigFunctionArgs) => {
        const isIp = isIpTest(wsDetails);
        const isMassEntryUnitPounds = isWetDensityMassEntryUnitPounds(wsDetails);
        const weightUnit = isIp && isMassEntryUnitPounds ? 'lb' : 'g';
        const volumeUnit = isIp ? 'ft³' : 'cm³';
        const weightVolumeUnit = isIp ? 'lb/ft³' : 'kg/m³';
        return {
          rowHeaders: [
            '',
            'Mold',
            `Mass (${weightUnit})`,
            `Volume (${volumeUnit})`,
            `Portion Mass (${weightUnit})`,
            'Water Added Target (%)',
            `Water to Add (${weightUnit})`,
            `Water Added (${weightUnit})`,
            'Actual Water Added (%)',
            `Wet Soil and Mold Mass (${weightUnit})`,
            `Wet Soil Mass (${weightUnit})`,
            `Wet Density (${weightVolumeUnit})`,
          ],
        };
      },

      'data.TestPortions.WaterAddedTargetPercent': { format: { nearest: 1 } },
      'data.TestPortions.WaterAddedPercent': { format: { nearest: 0.1 } },
      'data.Equipment.HammerFaceDiameter': (configArgs: IConfigFunctionArgs) => {
        const units = oPath.get(configArgs.worksheet, 'data.Equipment.HammerFaceNominalUnits');
        return {
          format: units === 'Metric' ? { nearest: 0.01 } : { nearest: 0.001 },
          uom: units === 'Metric' ? 'mm' : 'in',
        };
      },
      'data.Equipment.HammerFaceRadius': (configArgs: IConfigFunctionArgs) => {
        const units = oPath.get(configArgs.worksheet, 'data.Equipment.HammerFaceNominalUnits');
        return {
          format: units === 'Metric' ? { nearest: 0.01 } : { nearest: 0.001 },
          uom: units === 'Metric' ? 'mm' : 'in',
        };
      },
    },
    getLabelConfig('data'),
    getMeasuredUsingConfig('data'),
    getDerivedFromMeasurementsConfig('data'),
    {
      [`data.Equipment.CompactionBalance`]: getDefaultReadability,
    }
  );

const ip = {
  'data.Equipment.MouldVolume': { uom: 'ft³', format: { type: FormatTypes.SignificantFigures, precision: 4 } },

  'data.TestPortions.WaterAddedPercent': { format: { nearest: 0.1 } },
  'data.TestPortions.MouldVolume': { uom: 'ft³', format: { type: FormatTypes.SignificantFigures, precision: 4 } },
  'data.TestPortions.WetDensity': { uom: 'lb/ft³', format: { type: FormatTypes.SignificantFigures, precision: 4 } },
  'data.TestPortions.DryDensity': { uom: 'lb/ft³', format: { type: FormatTypes.SignificantFigures, precision: 4 } },

  'data.Results.MaximumDryDensity': { uom: 'lb/ft³', format: { type: FormatTypes.SignificantFigures, precision: 4 } },
  'data.Results.OptimumMoistureContent': { format: { nearest: 0.1 } },
  'data.Results.AdjustedMaximumDryDensity': {
    uom: 'lb/ft³',
    format: { type: FormatTypes.SignificantFigures, precision: 4 },
  },
  'data.Results.AdjustedOptimumMoistureContent': { format: { nearest: 0.1 } },
};

const metric = {
  'data.Equipment.MouldVolume': { uom: 'cm³', format: { type: FormatTypes.SignificantFigures, precision: 4 } },

  'data.TestPortions.WaterAddedPercent': { format: { nearest: 0.1 } },

  'data.TestPortions.MouldVolume': { uom: 'cm³', format: { type: FormatTypes.SignificantFigures, precision: 4 } },
  'data.TestPortions.WetDensity': { uom: 'kg/m³', format: { type: FormatTypes.SignificantFigures, precision: 4 } },
  'data.TestPortions.DryDensity': { uom: 'kg/m³', format: { type: FormatTypes.SignificantFigures, precision: 4 } },

  'data.Results.MaximumDryDensity': { uom: 'kg/m³', format: { type: FormatTypes.SignificantFigures, precision: 4 } },
  'data.Results.OptimumMoistureContent': { format: { nearest: 0.1 } },
  'data.Results.AdjustedMaximumDryDensity': {
    uom: 'kg/m³',
    format: { type: FormatTypes.SignificantFigures, precision: 4 },
  },
  'data.Results.AdjustedOptimumMoistureContent': { format: { nearest: 0.1 } },
};

const ipConfig = () => mergeConfig(config(), ip);
const metricConfig = () => mergeConfig(config(), metric);

export default {
  [qestIds.maximumDryDensity.AASHTO_T180.rev2015]: ipConfig,
  [qestIds.maximumDryDensity.AASHTO_T99.rev2015]: ipConfig,
  [qestIds.maximumDryDensity.AASHTO_T180.rev2015_metric]: metricConfig,
  [qestIds.maximumDryDensity.AASHTO_T99.rev2015_metric]: metricConfig,
};
